/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import dayjs from 'dayjs';
import {
  completeAssetView,
  globalAccountsView,
  makeSelectProjectTimelineItems,
  makeSelectProjectSumById,
  postMixedData,
} from '../../redux/reducers/data';
import { TransactionComponent } from './TransactionComponent';
import QuoteComponent from './QuoteComponent';
import GoalComponent from './GoalComponent';
import { clearHoveredProjectAccountId, setHoveredProjectAccountId } from './projectAssetHoverStore';

function DateSeparator({ dateArg }) {
  const { i18n } = useTranslation();

  return (
    <div id={`project-transaction-list-date-separator-${dayjs().format('YY-MM')}`} className="relative pb-1.5 flex items-center w-full">
      <div className="mx-4 flex-grow border-t border-gray-300" />
      <span className="px-8 uppercase tracking-widest text-sm text-gray-400">
        {new Date(dateArg).toLocaleDateString(i18n.language, {
          month: 'short',
          year: 'numeric',
          timeZone: 'UTC',
        })}
      </span>
      <div className="mx-4 flex-grow border-t border-gray-300" />
    </div>
  );
}
DateSeparator.propTypes = {
  dateArg: PropTypes.number.isRequired,
};

function DraggableTransaction(props) {
  const Element = props.element || 'div';
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.transaction.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none',
  };

  return (
    <Element ref={setNodeRef} style={style} {...listeners} {...attributes} className="w-full">
      <ErrorBoundary FallbackComponent={() => <div />}>
        <TransactionComponent {...props} />
      </ErrorBoundary>
    </Element>
  );
}
DraggableTransaction.propTypes = {
  element: PropTypes.string,
  transaction: PropTypes.objectOf(PropTypes.any).isRequired,
  project: PropTypes.objectOf(PropTypes.any).isRequired,
};
DraggableTransaction.defaultProps = {
  element: 'div',
};

export default function ProjectTransactionList({ project, setTransactionDialog, setQuoteDialog, setGoalDialog, projectAssets, filteredItems }) {
  const dispatch = useDispatch();
  const [items, setItems] = useState({});
  const [breaker, setBreaker] = useState(false);
  const [_, setActiveTransaction] = useState();
  const [visibleRecurringTransactions, setVisibleRecurringTransactions] = useState([]);

  const selectProjectTimelineItems = useMemo(makeSelectProjectTimelineItems, []);
  const selectProjectSumById = useMemo(makeSelectProjectSumById, []);
  const projectTimeline = useSelector((state) => selectProjectTimelineItems(state, project.id, filteredItems, !!project?.settings?.isIsolated));
  const projectSumAtDate = useSelector((state) => selectProjectSumById(state, project.id));

  const cav = useSelector(completeAssetView);
  const accounts = useSelector(globalAccountsView);
  const ibans = useSelector((state) => state.data.deposits.accounts.map((account) => account.iban));

  useEffect(() => {
    setVisibleRecurringTransactions(projectTimeline?.recurringDefaultVisible || []);
  }, [projectTimeline?.recurringDefaultVisible]);

  useEffect(() => () => clearHoveredProjectAccountId(), []);

  const derivedItems = useMemo(() => {
    const byMonth = projectTimeline?.itemsByMonth || {};
    const output = {};

    Object.keys(byMonth).forEach((monthKey) => {
      const monthItems = byMonth[monthKey] || [];
      const filteredMonthItems = monthItems.filter((item) => !item.recurringParentId || visibleRecurringTransactions.includes(item.recurringParentId));
      if (filteredMonthItems.length > 0) output[monthKey] = filteredMonthItems;
    });

    return output;
  }, [projectTimeline?.itemsByMonth, visibleRecurringTransactions]);

  useEffect(() => {
    if (!breaker) setItems(derivedItems);
  }, [derivedItems, breaker]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function findContainer(id) {
    if (id in items) {
      return id;
    }

    return Object.keys(items).find((key) => items[key].findIndex((item) => item.id === id) > -1);
  }

  function handleDragStart(event) {
    const { active } = event;
    const { id } = active;

    setBreaker(true);
    setActiveTransaction(items[findContainer(id)].find((item) => item.id === id));
  }

  function handleDragOver(event) {
    const { active, over } = event;
    if (!over) return;
    const { id } = active;
    const { id: overId } = over;

    const activeContainer = findContainer(id);
    const overContainer = findContainer(overId);

    if (!activeContainer || !overContainer || activeContainer === overContainer) {
      return;
    }

    setItems((prev) => {
      const activeItems = prev[activeContainer];
      const overItems = prev[overContainer];

      const activeIndex = activeItems.findIndex((item) => item.id === id);
      const overIndex = overItems.findIndex((item) => item.id === overId);

      let newIndex;
      if (overId in prev) {
        newIndex = overItems.length + 1;
      } else {
        const isBelowLastItem = over && overIndex === overItems.length - 1;
        const modifier = isBelowLastItem ? 1 : 0;
        newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
      }

      return {
        ...prev,
        [activeContainer]: [...prev[activeContainer].filter((item) => item.id !== active.id)],
        [overContainer]: [...prev[overContainer].slice(0, newIndex), prev[activeContainer][activeIndex], ...prev[overContainer].slice(newIndex, prev[overContainer].length)],
      };
    });
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || !active) return;
    const { id } = active;
    const { id: overId } = over;

    const activeContainer = findContainer(id);
    const overContainer = findContainer(overId);

    if (!activeContainer || !overContainer || activeContainer !== overContainer) {
      return;
    }

    const activeIndex = items[activeContainer].findIndex((item) => item.id === active.id);
    const overIndex = items[overContainer].findIndex((item) => item.id === overId);

    if (activeIndex !== overIndex) {
      const nextItems = arrayMove(items[overContainer], activeIndex, overIndex);
      setItems((prev) => ({
        ...prev,
        [overContainer]: nextItems,
      }));
      const toBeDispatched = nextItems.map((item, index) => ({ ...item, sortingOrderWithinMonth: index }));
      dispatch(postMixedData({ payload: toBeDispatched }));
      setTimeout(() => setBreaker(false), 5000);
    } else {
      setBreaker(false);
    }
    setActiveTransaction(null);
  }

  return (
    <div id="project-transactions-list" className="w-full lg:pr-2 flex flex-col gap-4 items-start">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
        {Object.keys(items || [])
          .sort((a, b) => (Number(a) > Number(b) ? 1 : -1))
          .filter((item) => items[item].length > 0)
          .map((month) => {
            const monthItems = items[month];
            const quoteItems = monthItems.filter((transaction) => 'quote' in transaction);
            const goalItems = monthItems.filter((transaction) => transaction.name);
            const transactionItems = monthItems.filter((transaction) => !('quote' in transaction) && !('name' in transaction));

            return (
              <React.Fragment key={Number(month)}>
                <DateSeparator dateArg={Number(month)} />
                {quoteItems.length > 0 && (
                  <div className="w-full flex flex-row justify-center items-center -mt-2 space-x-2">
                    {quoteItems.map((q) => (
                      <QuoteComponent quote={q} setQuoteDialog={setQuoteDialog} projectAssets={projectAssets} key={`${q.assetId}-${q.date}-${q.quote}`} />
                    ))}
                  </div>
                )}
                {goalItems.length > 0 && (
                  <div className="w-full flex flex-row justify-center items-center -mt-2">
                    {goalItems.map((g) => (
                      <GoalComponent key={`${g.date}`} goal={g} setGoalDialog={setGoalDialog} projectSumAtDate={projectSumAtDate} />
                    ))}
                  </div>
                )}
                {transactionItems.length > 0 && (
                  <SortableContext id={month} items={monthItems}>
                    {transactionItems.map((transaction) => (
                      <DraggableTransaction
                        key={transaction.id}
                        id={`project-transactions-item-${month}-${transaction.id}`}
                        transaction={transaction}
                        project={project}
                        setTransactionDialog={setTransactionDialog}
                        visibleRecurringTransactions={visibleRecurringTransactions}
                        setVisibleRecurringTransactions={setVisibleRecurringTransactions}
                        setAssetTileHover={setHoveredProjectAccountId}
                        clearAssetTileHover={clearHoveredProjectAccountId}
                        accounts={accounts}
                        ibans={ibans}
                        cav={cav}
                      />
                    ))}
                  </SortableContext>
                )}
              </React.Fragment>
            );
          })}
      </DndContext>
    </div>
  );
}
ProjectTransactionList.propTypes = {
  project: PropTypes.objectOf(PropTypes.any).isRequired,
  setTransactionDialog: PropTypes.func.isRequired,
  setQuoteDialog: PropTypes.func.isRequired,
  setGoalDialog: PropTypes.func.isRequired,
  projectAssets: PropTypes.arrayOf(PropTypes.any),
  filteredItems: PropTypes.arrayOf(PropTypes.any).isRequired,
};
ProjectTransactionList.defaultProps = {
  projectAssets: [],
};
